<template>
	<div class="gallery" :class="{ pressed }">
		<div class="gallery__slides" :style="galleryStyle">
			<div class="gallery__slides__drag" :style="slidesStyle">
				<slot />
			</div>
		</div>
		<div class="gallery__dots">
			<div
				v-for="i in numSlides"
				:key="i"
				class="gallery__dot"
				:class="{ active: i === page }"
			></div>
		</div>
	</div>
</template>

<script>
import button from '@/mixins/button'

const clamp = (n, min, max) => Math.max(min, Math.min(n, max))
const eventX = e => (e.touches ? e.touches[0] : e).clientX

// Velocity threshold for swiping between gallery pages
const SWIPE_VEL_THRESHOLD = 0.15

// Scroll tween settings
const TWEEN_OPTS = {
	duration: 200,
	ease: t => 1 - Math.pow(t - 1, 2)
}

export default {
	name: 'Gallery',
	data() {
		return {
			id: 0,
			slideMap: {},
			width: 0,
			height: 0,
			offset: 0,
			pageStart: 0,
			dragStart: 0,
			dragOffset: 0,
			dragVel: 0,
			lastUpdate: 0,
			lastPosition: 0,
			tween: null
		}
	},
	computed: {
		slides() {
			return Object.values(this.slideMap)
		},
		numSlides() {
			return this.slides.length
		},
		galleryStyle() {
			return {
				height: this.height + 'px'
			}
		},
		totalOffset() {
			return this.offset + (this.pressed ? this.dragOffset : 0)
		},
		slidesStyle() {
			return {
				transform: `translate3d(${this.totalOffset}px, 0, 0)`
			}
		},
		xRange() {
			return [-this.width * (this.numSlides - 1), 0]
		},
		page() {
			return Math.round(-this.totalOffset / this.width) + 1
		}
	},
	methods: {
		register(slide) {
			const id = this.id++
			this.$set(this.slideMap, id, slide)
			this.update()
			return id
		},
		deregister(id) {
			this.$delete(this.slideMap, id)
			this.update()
		},
		onDragStart(event) {
			this.dragStart = eventX(event)
			this.pageStart = this.page
		},
		onDragMove(event) {
			// Calculate drag offset
			const now = Date.now()
			const x = eventX(event)
			const range = this.xRange.map(n => n - this.offset)
			this.dragOffset = clamp(x - this.dragStart, ...range)

			// Calculate drag velocity
			const dx = x - this.lastPosition
			const dt = now - this.lastUpdate
			this.dragVel = dx / dt

			// Record last time and position of pointer for velocity calc later
			this.lastUpdate = Date.now()
			this.lastPosition = x
		},
		onDragStop() {
			this.offset = clamp(this.offset + this.dragOffset, ...this.xRange)

			// Different ease strategy depending on if pointer was moving
			if (Math.abs(this.dragVel) > SWIPE_VEL_THRESHOLD) {
				const dir = -Math.sign(this.dragVel)
				const dp = this.page - this.pageStart
				const target = clamp(this.page + dir - dp, 1, this.numSlides)
				this.tweenToPage(target)
			} else {
				this.tweenToPage(this.page)
			}

			// Reset offset and velocity
			this.dragOffset = 0
			this.dragVel = 0
		},
		tweenToPage(page) {
			// Cancel any existing tween
			this.cancelTween()

			// Calculate target and start new tween
			const to = (page - 1) * -this.width
			this.tween = this.addTween(
				{ from: this.offset, to, ...TWEEN_OPTS },
				x => (this.offset = x)
			)
		},
		cancelTween() {
			if (this.tween) {
				this.removeTween(this.tween)
				this.tween = null
			}
		},
		update() {
			// Re-measure, then tell all slides their indexes
			this.measure()
			this.slides.forEach((slide, index) => {
				slide.setIndex(index)
			})
		},
		measure() {
			const heights = this.slides.map(slide => slide.$el.offsetHeight)
			this.height = Math.max(...heights)
			this.width = this.$el.offsetWidth
		}
	},
	mounted() {
		this.measure()
		this.$on('down', this.onDragStart)
		this.$on('drag', this.onDragMove)
		this.$on('click', this.onDragStop)
	},
	beforeDestroy() {
		this.$off('down', this.onDragStart)
		this.$off('drag', this.onDragMove)
		this.$off('click', this.onDragStop)
	},
	mixins: [button({ scrollCancel: false })]
}
</script>

<style lang="scss" scoped>
@import '~scss/vars';
@import '~scss/mixins';

.gallery {
	width: 100%;

	&__slides {
		position: relative;
		touch-action: none;
		cursor: grab;

		&__drag {
			position: absolute;
			width: 100%;
			height: 100%;

			* {
				user-select: none;
			}
		}
	}

	&.pressed &__slides {
		cursor: grabbing;
	}

	&__dots {
		display: flex;
		justify-content: center;
		margin-top: 3rem;
	}

	&__dot {
		@include circle(0.4375rem, white);
		border-radius: 50%;
		&:not(:last-child) {
			margin-right: 0.625rem;
		}
		&.active {
			background-color: $blue-65;
		}
	}
}
</style>
